import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { AlertTriangle, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
});

const DangerZone = ({ onDataChanged }) => {
  const [open, setOpen] = useState(false);

  // Product cleanup state
  const [beforeDate, setBeforeDate] = useState('2026-05-20');
  const [productPreview, setProductPreview] = useState(null);
  const [productConfirm, setProductConfirm] = useState('');
  const [productBusy, setProductBusy] = useState(false);

  // Order cleanup state
  const [orderPreview, setOrderPreview] = useState(null);
  const [orderConfirm, setOrderConfirm] = useState('');
  const [orderBusy, setOrderBusy] = useState(false);

  const previewProducts = async () => {
    setProductBusy(true);
    try {
      const res = await axios.post(`${API}/admin/cleanup/products`, { before_date: beforeDate, dry_run: true }, getAuthHeaders());
      setProductPreview(res.data);
      setProductConfirm('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setProductBusy(false);
    }
  };

  const deleteProducts = async () => {
    setProductBusy(true);
    try {
      const res = await axios.post(`${API}/admin/cleanup/products`, { before_date: beforeDate, dry_run: false }, getAuthHeaders());
      toast.success(`Deleted ${res.data.deleted} products. ${res.data.remaining} remaining.`, { duration: 8000 });
      setProductPreview(null);
      setProductConfirm('');
      onDataChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Deletion failed');
    } finally {
      setProductBusy(false);
    }
  };

  const previewOrders = async () => {
    setOrderBusy(true);
    try {
      const res = await axios.post(`${API}/admin/cleanup/orders`, { dry_run: true }, getAuthHeaders());
      setOrderPreview(res.data);
      setOrderConfirm('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setOrderBusy(false);
    }
  };

  const deleteOrders = async () => {
    setOrderBusy(true);
    try {
      const res = await axios.post(`${API}/admin/cleanup/orders`, { dry_run: false }, getAuthHeaders());
      toast.success(`Cleared ${res.data.deleted_orders} orders and ${res.data.deleted_transactions} transactions.`, { duration: 8000 });
      setOrderPreview(null);
      setOrderConfirm('');
      onDataChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Deletion failed');
    } finally {
      setOrderBusy(false);
    }
  };

  return (
    <div className="mt-10 border border-red-200 rounded-lg overflow-hidden" data-testid="danger-zone">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-red-50 hover:bg-red-100 transition-colors"
        data-testid="danger-zone-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-red-700">
          <AlertTriangle className="w-4 h-4" />
          Danger Zone — Data Cleanup
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-red-600" /> : <ChevronDown className="w-4 h-4 text-red-600" />}
      </button>

      {open && (
        <div className="p-4 sm:p-5 bg-white grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Delete old products */}
          <div className="border border-slate-200 rounded-lg p-4">
            <h4 className="font-semibold text-slate-900 text-sm mb-1">Delete old products</h4>
            <p className="text-xs text-slate-500 mb-3">
              Permanently deletes every product created on or before the selected date. Orders and customers are untouched.
            </p>
            <div className="flex items-end gap-2 mb-3">
              <div className="flex-1">
                <Label htmlFor="cleanup-before-date" className="text-xs">Created on or before</Label>
                <Input
                  id="cleanup-before-date"
                  type="date"
                  value={beforeDate}
                  onChange={(e) => { setBeforeDate(e.target.value); setProductPreview(null); }}
                  className="mt-1"
                  data-testid="cleanup-before-date-input"
                />
              </div>
              <Button
                variant="outline"
                onClick={previewProducts}
                disabled={productBusy || !beforeDate}
                data-testid="cleanup-products-preview-btn"
              >
                {productBusy ? 'Checking…' : 'Preview'}
              </Button>
            </div>

            {productPreview && (
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm" data-testid="cleanup-products-preview">
                <p className="text-slate-900">
                  <span className="font-bold text-red-600">{productPreview.count}</span> of {productPreview.total_products} products will be deleted
                  ({productPreview.remaining_after} will remain).
                </p>
                {productPreview.sample?.length > 0 && (
                  <ul className="mt-2 text-xs text-slate-500 list-disc list-inside space-y-0.5">
                    {productPreview.sample.map((n, i) => <li key={i} className="truncate">{n}</li>)}
                    {productPreview.count > productPreview.sample.length && (
                      <li className="list-none text-slate-400">…and {productPreview.count - productPreview.sample.length} more</li>
                    )}
                  </ul>
                )}
                {productPreview.count > 0 && (
                  <div className="mt-3 space-y-2">
                    <Input
                      placeholder='Type DELETE to confirm'
                      value={productConfirm}
                      onChange={(e) => setProductConfirm(e.target.value)}
                      data-testid="cleanup-products-confirm-input"
                    />
                    <Button
                      variant="destructive"
                      className="w-full"
                      disabled={productConfirm !== 'DELETE' || productBusy}
                      onClick={deleteProducts}
                      data-testid="cleanup-products-delete-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Permanently delete {productPreview.count} products
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Clear all orders */}
          <div className="border border-slate-200 rounded-lg p-4">
            <h4 className="font-semibold text-slate-900 text-sm mb-1">Clear all orders & transactions</h4>
            <p className="text-xs text-slate-500 mb-3">
              Permanently deletes ALL orders, payment transactions and admin order notifications. Products and customer accounts are untouched. Use once before going live.
            </p>
            <Button
              variant="outline"
              onClick={previewOrders}
              disabled={orderBusy}
              data-testid="cleanup-orders-preview-btn"
            >
              {orderBusy ? 'Checking…' : 'Preview'}
            </Button>

            {orderPreview && (
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm mt-3" data-testid="cleanup-orders-preview">
                <p className="text-slate-900">
                  Will delete: <span className="font-bold text-red-600">{orderPreview.orders}</span> orders,{' '}
                  <span className="font-bold text-red-600">{orderPreview.payment_transactions}</span> payment transactions,{' '}
                  <span className="font-bold text-red-600">{orderPreview.admin_notifications}</span> notifications.
                </p>
                {(orderPreview.orders > 0 || orderPreview.payment_transactions > 0 || orderPreview.admin_notifications > 0) && (
                  <div className="mt-3 space-y-2">
                    <Input
                      placeholder='Type DELETE to confirm'
                      value={orderConfirm}
                      onChange={(e) => setOrderConfirm(e.target.value)}
                      data-testid="cleanup-orders-confirm-input"
                    />
                    <Button
                      variant="destructive"
                      className="w-full"
                      disabled={orderConfirm !== 'DELETE' || orderBusy}
                      onClick={deleteOrders}
                      data-testid="cleanup-orders-delete-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Permanently clear all order history
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DangerZone;
